import React, {
  useEffect,
} from 'react';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import {
  useQueryClient,
} from '@tanstack/react-query';
import {
  ProviderEntryConfigDTO,
} from 'dtos';
import {
  useUpsertProviderEntryConfig,
  useGetProviderEntryConfig,
} from 'pages/Dashboard/components/ReferringProvider/hooks';
import Dialog from 'pages/Dashboard/components/Dialog';
import Button from 'pages/Dashboard/components/Button';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  getProviderEntryConfigLookup,
  getProviderEntryConfigQueryKey,
  ProviderEntryConfigTarget,
} from 'pages/Dashboard/components/ReferringProvider/providerEntryConfigTarget';
import ProviderContactFields from 'pages/Dashboard/components/ReferringProvider/ProviderContactFields';
import {
  getEmptyProviderConfig,
  normalizeProviderConfigPayload,
} from 'pages/Dashboard/components/ReferringProvider/providerEntryConfig';

type Props = {
  target: ProviderEntryConfigTarget;
  onClose: () => void;
};

export default function ProviderContactConfigDialog({
  target,
  onClose,
}: Props) {
  const providerEntryId = target.kind === 'existing'
    ? target.providerEntryId
    : undefined;
  const queryKey = getProviderEntryConfigQueryKey(target);
  const lookup = getProviderEntryConfigLookup(target);
  const queryClient = useQueryClient();
  const formProps = useForm<ProviderEntryConfigDTO>({
    mode: 'all',
    defaultValues: getEmptyProviderConfig(providerEntryId),
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = formProps;

  const {
    data: providerConfig,
    isFetching,
    isLoading,
    isError,
    error,
  } = useGetProviderEntryConfig(target);

  const { mutateAsync: saveProviderConfig } = useUpsertProviderEntryConfig();

  useEffect(() => {
    reset({
      ...getEmptyProviderConfig(providerEntryId),
      ...(providerConfig ?? {}),
      providerEntryId,
      isPinned: providerConfig?.isPinned ?? false,
    });
  }, [providerConfig, providerEntryId, reset]);

  const onSubmit = async (formData: ProviderEntryConfigDTO) => {
    await saveProviderConfig(normalizeProviderConfigPayload(formData, target));
    await queryClient.invalidateQueries({
      queryKey,
    });
    onClose();
  };

  return (
    <Dialog
      open
      title="Provider Contact"
      onClose={onClose}
      onConfirm={() => {}}
      className="!w-96"
    >
      <FormProvider {...formProps}>
        <div className="populate-form flex flex-col gap-form-row">
          {(isLoading || isFetching) && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Spinner />
              Loading contact configuration...
            </div>
          )}
          {!isLoading && !isFetching && isError && (
            <p className="text-sm text-red-600" title={error instanceof Error ? error.message : undefined}>
              Failed to load provider contact configuration.
            </p>
          )}
          {!isLoading && !isFetching && !isError && lookup.kind === 'unavailable' ? (
            <p className="text-sm text-gray-500">
              Contact is unavailable until this provider is saved.
            </p>
          ) : null}
          {!isLoading && !isFetching && !isError && lookup.kind !== 'unavailable' && (
            <ProviderContactFields />
          )}
          <div className="flex justify-end gap-2">
            <Button
              variant="outlined"
              onClick={onClose}
            >
              Close
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={isSubmitting || isFetching}
              onClick={handleSubmit(onSubmit)}
            >
              Save
            </Button>
          </div>
        </div>
      </FormProvider>
    </Dialog>
  );
}
